<template>
  <cta-row
    v-if="globalPlayer && globalPlayer.currentSource"
    force-show
    :show="isShown"
    classes="global-player__cta-wrapper"
  >
    <div class="global-player overflow-hidden">
      <div class="d-flex">
        <div
          class="global-player__details d-flex align-items-center py-1 pl-1"
          @click.prevent="handleClick"
        >
          <div
            class="global-player__image flex-shrink-0"
            :style="{
              backgroundImage: `url(${globalPlayer.currentSource.audio.thumbnail})`
            }"
          />

          <div class="global-player__title">
            {{ globalPlayer.currentSource.audio.title }}
          </div>
        </div>
        <div
          class="global-player__actions flex-shrink-0 d-flex pr-1"
          data-cy="audio-play"
        >
          <v-button
            class="global-player__play-button"
            @click.stop.prevent="() => globalPlayer.toggle()"
            icon
            ghost
            primary
            round
          >
            <template v-if="globalPlayer.isLoading">
              <v-loader />
            </template>
            <template v-else-if="globalPlayer.hasFinished">
              <v-icon icon="replay" size="1.4rem" />
            </template>
            <template v-else-if="globalPlayer.isPlaying">
              <v-icon icon="pause" size="1.4rem" />
            </template>
            <template v-else>
              <v-icon icon="play_arrow" size="1.4rem" />
            </template>
          </v-button>

          <v-button
            class="opacity-60"
            ghost
            icon
            round
            small
            secondary
            @click.stop.prevent="teardown"
          >
            <v-icon icon="close" size="1.125rem" />
          </v-button>
        </div>
      </div>
      <div data-cy="global-player" class="global-player__audio">
        <div class="p-relative flex-grow-1">
          <input
            class="audio-player__progress-time --small"
            type="range"
            min="0"
            max="100"
            step="0.01"
            :style="{
              '--value': globalPlayer.currentTimeAsPercentage + '%'
            }"
            :value="globalPlayer.currentTimeAsPercentage"
            @input="e => globalPlayer.seek(e.target.value)"
          /><progress
            :class="[
              'audio-player__progress-buffer',
              { '--is-loading': globalPlayer.isLoading }
            ]"
            min="0"
            max="100"
            :value="globalPlayer.buffered * 100"
            >% buffered</progress
          >
        </div>
      </div>
    </div>
  </cta-row>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex"

import CtaRow from "@/components/CtaRow"

export default {
  components: {
    CtaRow
  },

  data() {
    return {
      isShown: false
    }
  },

  methods: {
    ...mapActions("media", ["setupGlobalPlayer"]),
    ...mapMutations("media", ["setGlobalPlayer"]),

    teardown() {
      this.globalPlayer.teardown()
      this.setGlobalPlayer(null)
    },

    updateIsShown() {
      if (!this.globalPlayer || !this.globalPlayer.currentSource) {
        this.isShown = false
        return
      }

      if (this.$route.path === this.globalPlayer.currentSource.page.path) {
        this.isShown = false
      } else {
        this.isShown = true
      }
    },

    handleClick() {
      if (!this.globalPlayer.currentSource) return

      const source = this.globalPlayer.currentSource

      if (source.page && source.page.path) {
        this.$router.push(source.page.path)

        // TODO: Scroll to audio element
      }
    }
  },

  mounted() {
    this.setupGlobalPlayer()
  },

  computed: {
    ...mapGetters("media", ["globalPlayer"])
  },

  watch: {
    "$route.path"() {
      this.updateIsShown()
    },

    "globalPlayer.currentSource"() {
      this.updateIsShown()
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";
@import "@/assets/styles/partials/audio";

.global-player__cta-wrapper {
  z-index: $modal--z-index - 5;
}

.global-player {
  width: 100%;
  cursor: pointer;
  z-index: $video--controls-z-index;
  width: 100%;
  box-shadow: $box-shadow--small, $box-shadow--large;
  background-color: $color-ui--white;
  border-radius: var(--border-radius);
  margin-bottom: 0.25rem;

  .global-player__audio {
    width: 100%;
    bottom: 0;
    z-index: $video--controls-z-index;
    height: 4px;

    display: flex;
    align-items: center;
    padding-left: 0;
    padding-right: 0;
  }

  .global-player__image {
    height: 38px;
    width: 38px;
    background-size: cover;
    background-position: center;
    border-radius: $size--1;
    margin-right: $size--2;
  }

  .global-player__details {
    max-width: calc(100% - 88px);
  }

  .global-player__title {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    color: $color-ui--black;
  }

  .global-player__actions {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
  }

  .global-player__play-button {
    .v-loader {
      width: $size--6;
      height: $size--6;
      min-width: $size--6;
      min-height: $size--6;
    }
  }

  .global-player__audio {
    input[type="range"].audio-player__progress-time {
      color: $color-ui--grey-60;

      @mixin plyr-range-thumb-override() {
        height: 0;
        border: 0;
        width: 0;
        background: transparent;
      }

      @mixin plyr-range-track-override() {
        border-radius: 0;
      }

      &::-webkit-slider-thumb {
        @include plyr-range-thumb-override();
      }

      &::-ms-thumb {
        @include plyr-range-thumb-override();
      }

      &::-webkit-slider-runnable-track {
        @include plyr-range-track-override();
      }

      &::-moz-range-track {
        @include plyr-range-track-override();
      }

      &::-ms-track {
        @include plyr-range-track-override();
      }
    }
  }

  progress.audio-player__progress-buffer {
    &::-webkit-progress-bar {
      border-radius: 0;
    }

    &::-moz-progress-bar {
      border-radius: 0;
    }
  }
}
</style>
